import React, { useContext } from "react";
import { PlanTemplate } from "./plan-templates";
import { CircleChevronRight } from "lucide-react";
import { Separator } from "@pushpress/pushpress-ui";
import { AppContext } from "../../../context/app";
import { getCurrencySymbol } from "../../../utils/currencyUtils";
import AnimatedPrice from "./AnimatedPrice";

interface PlanTemplateCardProps {
  template: PlanTemplate;
  onClick: () => void;
  isLoading: boolean;
  shouldFetchPriceRecommendations: boolean;
}

export const PlanTemplateCard: React.FC<PlanTemplateCardProps> = ({
  template,
  onClick,
  isLoading,
  shouldFetchPriceRecommendations,
}) => {
  const app = useContext(AppContext);
  const { client } = app;
  const currencySymbol = getCurrencySymbol(client.currencyIso);
  const { recommendedPrice, name, description, benefits, frequency } = template;

  const displayPrice = recommendedPrice !== undefined ? recommendedPrice : "--";

  return (
    <button
      onClick={onClick}
      className="tw-w-full group tw-p-3 tw-border tw-rounded-lg tw-cursor-pointer hover:tw-shadow-md tw-transition-shadow tw-text-left"
    >
      <div className="tw-grid md:tw-grid-cols-[180px_1px_1fr_auto] tw-items-stretch">
        <div className="tw-pl-3 tw-pr-6 tw-py-6 tw-flex tw-flex-col tw-justify-center tw-items-center">
          {shouldFetchPriceRecommendations && recommendedPrice !== undefined ? (
            <AnimatedPrice
              isLoading={isLoading}
              price={recommendedPrice}
              currencyIso={client.currencyIso}
            />
          ) : (
            <span className="tw-text-3xl tw-font-bold tw-text-gray-600">
              {currencySymbol}
              {displayPrice}
            </span>
          )}

          <p className="tw-text-gray-600">{frequency}</p>
        </div>

        <Separator className="tw-hidden md:tw-block" orientation="vertical" />
        <div className="tw-py-2 tw-px-6 tw-text-left">
          <h3 className="tw-text-lg tw-font-medium tw-mb-2">{name}</h3>

          <p className="tw-text-gray-500 tw-mb-2">{description}</p>

          <ul className="tw-space-y-1">
            {benefits.map((benefit, index) => (
              <li key={index} className="tw-flex tw-items-start tw-gap-1">
                <span className="tw-text-gray-500">•</span>
                <span className="tw-text-gray-500">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="tw-hidden md:tw-flex tw-items-center tw-pr-6">
          <CircleChevronRight className="tw-text-gray-600 tw-w-10 tw-h-10 tw-stroke-1" />
        </div>
      </div>
    </button>
  );
};
